import { useMemo } from 'react'

import { useQuery } from '@apollo/client/react'

import type { TagColor } from '@buffer-mono/popcorn'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { formatDate } from '~publish/helpers/dateFormatters'

import type { ContentItem } from './ContentCarousel'

/** Number of recommendations requested for the Home carousel. */
const RECOMMENDED_CONTENT_LIMIT = 4

export const RecommendedContentQuery = graphql(`
  query RecommendedContent($input: RecommendedContentInput!, $first: Int) {
    recommendedContent(input: $input, first: $first) {
      edges {
        node {
          id
          contentType
          contentPublishedAt
          title
          url
          thumbnailUrl
        }
      }
    }
  }
`)

type ApiItem = ResultOf<
  typeof RecommendedContentQuery
>['recommendedContent']['edges'][number]['node']

/**
 * Maps the content type to its display tag. Returns `undefined` for unknown
 * types so the card renders without a tag rather than guessing a label.
 */
function contentTypeTag(
  contentType: ApiItem['contentType'],
): { label: string; color: TagColor } | undefined {
  switch (contentType) {
    case 'blogPost':
      return { label: 'Blog post', color: 'blue-light' }
    case 'youtubeVideo':
      return { label: 'Video', color: 'red-light' }
    default:
      return undefined
  }
}

/**
 * The catalog stores the publish date as a UTC calendar date, so the label is
 * formatted in UTC. Formatting in the viewer's time zone would shift the
 * date by a day west of Greenwich.
 */
function publishedAtLabel(
  contentPublishedAt: ApiItem['contentPublishedAt'],
): string | undefined {
  if (!contentPublishedAt) {
    return undefined
  }

  return formatDate(contentPublishedAt, {
    dateFormat: 'MMM d, yyyy',
    timeZone: 'UTC',
    includeTimeZone: false,
  })
}

function mapItem(node: ApiItem): ContentItem {
  const tag = contentTypeTag(node.contentType)

  return {
    id: node.id,
    title: node.title,
    url: node.url,
    imageUrl: node.thumbnailUrl,
    category: tag?.label,
    categoryColor: tag?.color,
    publishedAtLabel: publishedAtLabel(node.contentPublishedAt),
  }
}

type UseRecommendedContentResult = {
  items: ContentItem[]
  loading: boolean
}

/**
 * Fetches curated blog/video recommendations for the current organization,
 * personalised server-side by its connected channels.
 *
 * @see graphql/src/recommended-content/queries.graphql
 */
export function useRecommendedContent(): UseRecommendedContentResult {
  const organizationId = useOrganizationId()

  const { data, loading } = useQuery(RecommendedContentQuery, {
    variables: {
      input: { organizationId: organizationId ?? '' },
      first: RECOMMENDED_CONTENT_LIMIT,
    },
    skip: !organizationId,
  })

  const items = useMemo(
    () =>
      data?.recommendedContent.edges.map((edge) => mapItem(edge.node)) ?? [],
    [data],
  )

  return { items, loading }
}
